import type { NavigateOptions } from '@tanstack/react-router';

import type { AppRouterContext } from 'providers/AppRouter';
import { readOppFlowFlags } from 'router/loaders/utils/oppFlowFlags';
import { getOppByIdQueryOptions } from 'services/partnersSelfSignup/opportunity';

type OpportunityOnVerifiedParams = {
  context: Pick<AppRouterContext, 'queryClient' | 'growthbook'>;
  oppId: string;
};

/**
 * Where the Terms & Conditions flow sends a Partner whose email is verified: the Terms & Conditions page. Reads the Opportunity, which is what the page shows.
 *
 * Throws when its work fails, including when the server does not accept the hash (the loop rule's host part, see `OnVerified`). The Opportunity query throws on the `200` with `requireAuthentication: true` that the Opportunity routes answer then. Without that, the Terms & Conditions route would send the Partner back to email validation, and OTP moving on by itself would loop.
 */
export function createOpportunityOnVerified({
  context,
  oppId,
}: OpportunityOnVerifiedParams) {
  const { queryClient, growthbook } = context;

  return async (): Promise<NavigateOptions> => {
    // Cached data is already the server's acceptance, because only an accepted answer is stored. With no data, this fetches.
    await queryClient.query(
      getOppByIdQueryOptions({ oppId, ...readOppFlowFlags(growthbook) }),
    );

    return {
      to: '/opportunity/$oppId/terms-and-conditions',
      params: { oppId },
      replace: true,
    };
  };
}
